import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import {
  About_Us_Access_Icon,
  About_Us_Clarity_Icon,
  About_Us_Global_Icon,
  About_Us_Hero_Img,
  About_Us_Map_Img,
  About_Us_Mission_Img,
  About_Us_Transparency_Icon,
  AI_BG_NEW,
  Use_Case_Bg,
} from "../../images";
import BookConsultation from "../book-a-consultation";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function AboutUs() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

    const helmetData = new HelmetData({});

  const why = [
    {
      icon: About_Us_Clarity_Icon,
      title: "Clarity over complexity",
      text: "Data should explain, not confuse.",
    },
    {
      icon: About_Us_Access_Icon,
      title: "Accessibility for all",
      text: "Analytics and AI shouldn’t be reserved for specialists.",
    },
    {
      icon: About_Us_Transparency_Icon,
      title: "Transparency first",
      text: "No black boxes, just insights you can trust.",
    },
    {
      icon: About_Us_Global_Icon,
      title: "Global impact",
      text: "From startups to multinationals, data should drive progress everywhere.",
    },
  ];
  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>About Analytics Intelligence Africa | Enterprise AI &amp; Data Solutions Across Africa</title>

        <link
          rel="canonical"
          href="https://africa.analyticsintelligence.com/about-us"
        />

        <meta
          name="description"
          content="Analytics Intelligence Africa sources, labels, and structures high-quality African data to power AI and analytics that reflect local realities. Learn about our mission, vision, and growing footprint across Nigeria, Ghana, Kenya, South Africa, Rwanda, and beyond."
        />

        <meta
          name="keywords"
          content="Analytics Intelligence Africa, about Analytics Intelligence Africa, African AI company, AI data company Africa, data annotation Africa, AI data labeling Africa, enterprise AI Africa, AI infrastructure Africa, local language LLMs Africa, AI analytics Africa, African data pipelines, inclusive AI Africa, AI company Nigeria, AI company Ghana, AI company Kenya, AI company South Africa, AI startup Africa"
        />

        <meta property="og:title" content="About Analytics Intelligence Africa | Enterprise AI & Data Solutions" />
        <meta property="og:description" content="Sourcing, labeling, and structuring high-quality African data to power AI and analytics that reflect local realities — Analytics Intelligence Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/about-us" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About Analytics Intelligence Africa | Enterprise AI & Data Solutions" />
        <meta name="twitter:description" content="Sourcing, labeling, and structuring high-quality African data to power AI and analytics that reflect local realities — Analytics Intelligence Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "url": "https://africa.analyticsintelligence.com/about-us",
            "name": "About Analytics Intelligence Africa",
            "description": "Analytics Intelligence Africa sources, labels, and structures high-quality African data to power AI and analytics that reflect local realities.",
            "mainEntity": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com",
              "logo": "https://africa.analyticsintelligence.com/favicon.png",
              "description": "Analytics Intelligence Africa is an enterprise AI and data services company delivering AI infrastructure, local language LLMs, data annotation, model evaluation, and analytics solutions across Africa.",
              "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Uganda", "Africa"]
            }
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-20 lg:gap-28 xl:gap-36  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  About Us
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    Accurate Labelled data,
                    <br className="hidden xl:block" /> Powered by AI
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    We source, label, and structure high-quality African data,
                    building the foundation for analytics and AI that truly
                    reflect local realities. By breaking down barriers to
                    access, we make advanced insights and AI tools available to
                    every team, not just technical ones, helping businesses and
                    innovators across Africa and beyond build smarter, more
                    inclusive solutions.
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full max-w-[680px]"
                src={About_Us_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="w-full relative overflow-hidden bg-white py-[80px] md:py-[100px]">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[350px] xl:w-[350px] bg-[#7AA8FF] blur-[180px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="flex flex-col items-center gap-8 md:grid md:gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              <div className="basis-2/4 space-y-8">
                <div className="space-y-2">
                  <h2 class="text-2xl md:text-[32px] font-extrabold text-black text-center md:text-left">
                    Our Mission
                  </h2>

                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-black text-center md:text-left">
                      To lead the global transition from legacy BI reporting to
                      AI-native analytics, enabling organisations to
                      operationalise secure data pipelines, intelligence,
                      reasoning, and decision automation at scale.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <h2 class="text-2xl md:text-[32px] font-extrabold text-black text-center md:text-left">
                    Our Vision
                  </h2>

                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-black text-center md:text-left">
                      To become the backbone infrastructure enabling enterprise
                      AI reasoning, powering agent-driven analytics, contextual
                      intelligence, and secure model execution across
                      industries.
                    </p>
                  </div>
                </div>
              </div>

              <img
                src={About_Us_Mission_Img}
                alt="computer illustration"
                className="h-auto w-full max-w-[500px] object-contain"
              />
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-10 text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                What We Believe
              </h1>
            </div>

            <div className="grid grid-cols-12 gap-y-8 sm:gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 xl:col-span-3 bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full relative overflow-hidden bg-white py-[80px] md:py-[100px]">
          <div className="mx-auto container">
            <div className="h-[300px] w-[300px] xl:h-[350px] xl:w-[350px] bg-[#7AA8FF] blur-[180px] absolute bottom-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-10 md:mb-20 text-center">
              <span className="text-[#318AF5]">Our Locations</span>
              <h1 className="mb-6 mt-4 text-2xl font-bold tracking-tight md:text-[36px]">
                Growing footprint across UK and Africa
              </h1>
              <p className="mx-auto max-w-5xl font-medium text-sm sm:text-base md:text-lg xl:text-xl text-gray-700">
                We operate from our headquarters in the United Kingdom, with
                active expansions in Nigeria, Ghana, Kenya, Rwanda, and South
                Africa — building AI solutions that are locally relevant and
                globally competitive.
              </p>
            </div>

            <img
              src={About_Us_Map_Img}
              alt="computer illustration"
              className="h-auto w-full object-cover"
            />
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Your industry's future takes shape here"
            text="With [AI], Do More with Your Data. Turn your data into real opportunities, helping you make smarter decisions, streamline operations, and discover new ways to grow."
            btnText="Get Started"
          />
        </NewFooter>
      </main>
    </>
  );
}
